import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Shield, FileText, Trash2, Eye, Download, AlertTriangle,
  Check, Clock, Send, ChevronRight, X, Lock, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { showFriendlyError } from '../utils/friendlyErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrivacyCenterPage() {
  const { user, getAuthHeader } = useAuth();
  const [consents, setConsents] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWithdraw, setShowWithdraw] = useState(null);
  const [showRequest, setShowRequest] = useState(false);
  const [requestForm, setRequestForm] = useState({ type: 'access', description: '', child_id: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [consentsRes, requestsRes] = await Promise.all([
        axios.get(`${API}/privacy/consents`, getAuthHeader()).catch(() => ({ data: { consents: [] } })),
        axios.get(`${API}/privacy/requests`, getAuthHeader()).catch(() => ({ data: { requests: [] } })),
      ]);
      setConsents(consentsRes.data.consents || []);
      setRequests(requestsRes.data.requests || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawConsent = async (childId, childName) => {
    if (!window.confirm(`Are you sure you want to withdraw consent for ${childName}? Their account will be deactivated and data deleted within 30 days.`)) return;
    try {
      await axios.post(`${API}/privacy/withdraw-consent`, { child_id: childId, reason: 'Parent requested withdrawal' }, getAuthHeader());
      toast.success(`Consent withdrawn for ${childName}. Account deactivated.`);
      setShowWithdraw(null);
      fetchData();
    } catch (e) {
      showFriendlyError(e, { fallback: 'Failed to withdraw consent' });
    }
  };

  const handleSubmitRequest = async () => {
    if (!requestForm.description.trim()) {
      toast.error('Please describe your request');
      return;
    }
    try {
      await axios.post(`${API}/privacy/data-request`, requestForm, getAuthHeader());
      toast.success('Privacy request submitted. We will respond within 30 business days.');
      setShowRequest(false);
      setRequestForm({ type: 'access', description: '', child_id: '' });
      fetchData();
    } catch (e) {
      toast.error('Failed to submit request');
    }
  };

  const children = user?.linked_children || [];
  const statusColors = { pending: 'text-amber-400', processing: 'text-blue-400', resolved: 'text-emerald-400', denied: 'text-red-400' };

  if (loading) return <AppLayout><div className="flex justify-center py-20"><LoadingSpinner /></div></AppLayout>;

  return (
    <AppLayout>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
            <Shield className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h1 className="font-outfit text-2xl font-semibold text-white">Privacy Center</h1>
            <p className="text-slate-300 text-sm">Manage consent, data access, and privacy for your family</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {[
          { icon: Eye, title: 'Request Data Access', desc: 'View all data we store about your family', color: 'blue', action: () => { setRequestForm({ ...requestForm, type: 'access' }); setShowRequest(true); } },
          { icon: FileText, title: 'Request Correction', desc: 'Fix inaccurate information', color: 'amber', action: () => { setRequestForm({ ...requestForm, type: 'correction' }); setShowRequest(true); } },
          { icon: Trash2, title: 'Request Deletion', desc: 'Delete all records (30-day process)', color: 'red', action: () => { setRequestForm({ ...requestForm, type: 'deletion' }); setShowRequest(true); } },
        ].map((item, i) => (
          <button key={i} onClick={item.action} className={`p-5 rounded-xl border border-${item.color}-500/20 bg-${item.color}-500/5 hover:bg-${item.color}-500/10 text-left transition-colors`} data-testid={`privacy-${item.title.toLowerCase().replace(/ /g, '-')}`}>
            <item.icon className={`w-6 h-6 text-${item.color}-400 mb-3`} />
            <h3 className="text-white font-semibold text-sm">{item.title}</h3>
            <p className="text-slate-300 text-xs mt-1">{item.desc}</p>
          </button>
        ))}
      </div>

      {/* Request Form Modal */}
      {showRequest && (
        <GlassCard className="mb-8 border-blue-500/30">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">New Privacy Request</h3>
            <button onClick={() => setShowRequest(false)} className="text-slate-300 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-slate-300 text-sm block mb-1">Request Type</label>
              <select value={requestForm.type} onChange={e => setRequestForm({ ...requestForm, type: e.target.value })} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm">
                <option value="access">Data Access, View stored data</option>
                <option value="correction">Data Correction, Fix inaccuracies</option>
                <option value="deletion">Data Deletion, Delete all records</option>
              </select>
            </div>
            {children.length > 0 && (
              <div>
                <label className="text-slate-300 text-sm block mb-1">Related Child (optional)</label>
                <select value={requestForm.child_id} onChange={e => setRequestForm({ ...requestForm, child_id: e.target.value })} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm">
                  <option value="">All family members</option>
                  {children.map(c => <option key={c.child_id} value={c.child_id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-slate-300 text-sm block mb-1">Description</label>
              <textarea value={requestForm.description} onChange={e => setRequestForm({ ...requestForm, description: e.target.value })} rows={3} placeholder="Describe what data you need or what should be changed..." className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm resize-none" />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSubmitRequest} className="bg-blue-600 hover:bg-blue-500">
                <Send className="w-4 h-4 mr-2" /> Submit Request
              </Button>
              <Button variant="ghost" onClick={() => setShowRequest(false)} className="text-slate-300">Cancel</Button>
            </div>
            <p className="text-slate-300 text-xs">Agentic FuturePath AI LLC will respond within 30 business days. Contact: privacy@futurepathAI.com</p>
          </div>
        </GlassCard>
      )}

      {/* Consent Records */}
      <h2 className="text-white font-semibold text-lg mb-4">Consent Records</h2>
      {consents.length > 0 ? (
        <div className="space-y-3 mb-8">
          {consents.map(consent => (
            <GlassCard key={consent.id} className="!p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  <div>
                    <p className="text-white text-sm font-medium capitalize">{consent.document_type?.replace(/_/g, ' ')}</p>
                    <p className="text-slate-300 text-xs">
                      {consent.child_name}, Signed {new Date(consent.signed_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="text-emerald-400 border-emerald-500/30 text-xs">
                  <Check className="w-3 h-3 mr-1" /> Active
                </Badge>
              </div>
            </GlassCard>
          ))}
        </div>
      ) : (
        <GlassCard className="!p-6 text-center mb-8">
          <p className="text-slate-300 text-sm">No consent records found yet.</p>
        </GlassCard>
      )}

      {/* Withdraw Consent */}
      {children.length > 0 && (
        <>
          <h2 className="text-white font-semibold text-lg mb-4">Withdraw Consent</h2>
          <GlassCard className="mb-8">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium text-sm">Important</p>
                <p className="text-slate-300 text-xs">Withdrawing consent will deactivate your child's account. All data will be permanently deleted within 30 calendar days. This action cannot be undone.</p>
              </div>
            </div>
            <div className="space-y-2">
              {children.map(child => (
                <div key={child.child_id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                  <span className="text-slate-300 text-sm">{child.name}</span>
                  <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs" onClick={() => handleWithdrawConsent(child.child_id, child.name)} data-testid={`withdraw-${child.child_id}`}>
                    Withdraw Consent
                  </Button>
                </div>
              ))}
            </div>
          </GlassCard>
        </>
      )}

      {/* Privacy Requests History */}
      <h2 className="text-white font-semibold text-lg mb-4">Request History</h2>
      {requests.length > 0 ? (
        <div className="space-y-2">
          {requests.map(req => (
            <GlassCard key={req.id} className="!p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-medium capitalize">{req.type} Request</p>
                  <p className="text-slate-300 text-xs">{req.description?.slice(0, 80)}{req.description?.length > 80 ? '...' : ''}</p>
                  <p className="text-slate-300 text-xs mt-1">{new Date(req.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`text-xs font-medium capitalize ${statusColors[req.status]}`}>
                  {req.status === 'pending' && <Clock className="w-3 h-3 inline mr-1" />}
                  {req.status}
                </span>
              </div>
            </GlassCard>
          ))}
        </div>
      ) : (
        <GlassCard className="!p-6 text-center">
          <p className="text-slate-300 text-sm">No privacy requests submitted yet.</p>
        </GlassCard>
      )}

      <div className="mt-8 p-4 rounded-xl bg-slate-800/30 border border-slate-700/30">
        <p className="text-slate-300 text-xs text-center">
          <Lock className="w-3 h-3 inline mr-1" />
          Agentic FuturePath AI LLC, Privacy Department, privacy@futurepathAI.com, All requests processed within 30 business days
        </p>
      </div>
    </AppLayout>
  );
}
